<template>
  <div class="flex items-center justify-center min-h-screen bg-gray-50 px-4">
    <div class="bg-white p-8 rounded-2xl shadow-md w-full max-w-md">
      <h2 class="text-2xl font-semibold text-center text-gray-800 mb-2">Reset Password</h2>
      <p class="text-gray-600 text-sm text-center mb-6">
        Enter your new password below to reset your account password.
      </p>

      <form @submit.prevent="handlePasswordReset" class="space-y-4">
        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <InputText
            v-model="resetForm.email"
            type="email"
            class="w-full"
            placeholder="Enter your email"
            readonly
          />
        </div>

        <!-- Token (hidden but available for safety/debugging) -->
        <InputText v-model="resetForm.token" type="hidden" />

        <!-- New Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">New Password</label>
          <Password
            v-model="resetForm.password"
            toggleMask
            inputClass="w-full"
            class="w-full"
            placeholder="Enter new password"
          />
        </div>

        <!-- Confirm Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
          <Password
            v-model="resetForm.password_confirmation"
            toggleMask
            inputClass="w-full"
            class="w-full"
            placeholder="Re-enter new password"
          />
        </div>

        <Button
          type="submit"
          label="Reset Password"
          class="w-full bg-primary hover:bg-primary/90 text-white border-none mt-3"
          :loading="isResetting"
        />

        <div class="text-center mt-6">
          <RouterLink to="/login" class="text-sm text-gray-600 hover:text-primary">
            ← Back to Login
          </RouterLink>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { RESET_PASSWORD } from '@/graphql';
import { PasswordBroker, type ResetPasswordInput } from '@/types/composition-functions';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const isResetting = ref(false);

// Read straight from the query so the gate is correct on first paint — reading
// them in onMounted flashes the form before we know the link is valid.
const linkToken = computed(() => route.query.token?.toString().trim() || '');
const linkEmail = computed(() => route.query.email?.toString().trim() || '');

// The reset mail carries the broker the token was issued for (staff vs student).
// Sending the wrong one — or none — makes the backend look the token up in the
// wrong table and reject a perfectly valid reset.
const linkBroker = computed<PasswordBroker | undefined>(() => {
  const broker = route.query.broker?.toString().toUpperCase();
  if (broker === PasswordBroker.Students) return PasswordBroker.Students;
  if (broker === PasswordBroker.Users) return PasswordBroker.Users;
  return undefined;
});

const resetForm = ref<ResetPasswordInput>({
  email: '',
  token: '',
  password: '',
  password_confirmation: '',
});

watchEffect(() => {
  resetForm.value.token = linkToken.value;
  resetForm.value.email = linkEmail.value;
});

const { mutate: resetPasswordMutation } = useMutation(RESET_PASSWORD);

async function handlePasswordReset() {
  if (!resetForm.value.password || !resetForm.value.password_confirmation) {
    toast.add({
      severity: 'warn',
      summary: 'Missing Fields',
      detail: 'Please fill in both password fields.',
      life: 3000,
    });
    return;
  }

  if (resetForm.value.password !== resetForm.value.password_confirmation) {
    toast.add({
      severity: 'warn',
      summary: 'Mismatch',
      detail: 'Passwords do not match.',
      life: 3000,
    });
    return;
  }

  try {
    isResetting.value = true;

    const { data } = (await resetPasswordMutation({
      input: {
        email: resetForm.value.email.toLowerCase(),
        token: resetForm.value.token,
        password: resetForm.value.password,
        password_confirmation: resetForm.value.password_confirmation,
        ...(linkBroker.value ? { broker: linkBroker.value } : {}),
      },
    }))!;

    const response = data?.resetPassword;

    if (response?.success) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response.message || 'Password reset successfully!',
        life: 3000,
      });

      setTimeout(() => {
        router.push('/login');
      }, 2000);
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: response?.message || 'Password reset failed.',
        life: 4000,
      });
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Something went wrong.';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 4000,
    });
  } finally {
    isResetting.value = false;
  }
}
</script>

<style scoped>
label {
  color: #374151;
}
</style>
